import { useNavigate } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView, useMotionValue, useSpring } from 'framer-motion';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import {
  ArrowRight, Play, Star, Zap, Target, Brain, Users,
  FileText, Calculator, Shield, TrendingUp, ChevronRight
} from 'lucide-react';

/* ── Animated counter hook ── */
function useCounter(end, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  useEffect(() => {
    if (!startOnView || !inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration, startOnView]);
  return { count, ref };
}

/* ── Apple-style sequential reveal wrapper ── */
function Reveal({ children, delay = 0, y = 48, className = '', scale = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y, scale: scale ? 0.97 : 1, filter: 'blur(4px)' }}
      animate={inView ? { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' } : {}}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

/* ── Floating orbs CSS animation ── */
function FloatingOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute w-[500px] h-[500px] rounded-full opacity-[0.07] animate-float-slow"
        style={{ background: 'radial-gradient(circle, #14B8A6, transparent 70%)', top: '10%', left: '-10%' }} />
      <div className="absolute w-[400px] h-[400px] rounded-full opacity-[0.05] animate-float-medium"
        style={{ background: 'radial-gradient(circle, #3B82F6, transparent 70%)', top: '30%', right: '-5%' }} />
      <div className="absolute w-[300px] h-[300px] rounded-full opacity-[0.06] animate-float-fast"
        style={{ background: 'radial-gradient(circle, #F5841F, transparent 70%)', bottom: '10%', left: '20%' }} />
    </div>
  );
}

/* ── Animated SVG lines decoration ── */
function AnimatedLines({ className = '' }) {
  return (
    <svg className={`absolute opacity-[0.04] ${className}`} width="400" height="400" viewBox="0 0 400 400" fill="none">
      <motion.circle cx="200" cy="200" r="150" stroke="#14B8A6" strokeWidth="0.5"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
        viewport={{ once: true }} transition={{ duration: 2, ease: 'easeInOut' }} />
      <motion.circle cx="200" cy="200" r="100" stroke="#3B82F6" strokeWidth="0.5"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
        viewport={{ once: true }} transition={{ duration: 2, delay: 0.3, ease: 'easeInOut' }} />
      <motion.circle cx="200" cy="200" r="50" stroke="#F5841F" strokeWidth="0.5"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
        viewport={{ once: true }} transition={{ duration: 2, delay: 0.6, ease: 'easeInOut' }} />
    </svg>
  );
}

/* ── HOW IT WORKS: Apple scroll-pinned sequential reveal ── */
const STEPS = [
  { step: '01', icon: FileText, title: 'Upload Resume', desc: 'Start by sharing your professional story. We extract your skills, experience, and career trajectory automatically.', color: '#0D9488' },
  { step: '02', icon: Brain, title: 'Take Assessments', desc: 'Complete our multi-phase Career Intelligence assessments. 16 carefully designed questions that decode your decision patterns.', color: '#3B82F6' },
  { step: '03', icon: Target, title: 'Unlock Persona', desc: 'Receive your unique Career Persona. A 12-dimension profile that maps your strengths, values, and optimal career paths.', color: '#F5841F' },
  { step: '04', icon: TrendingUp, title: 'Get Strategy', desc: 'Get a personalized career roadmap with expert advisory insights. Clear next steps, not generic advice.', color: '#0D9488' },
];

function HowItWorksSequential({ navigate }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end end'] });
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (v) => {
      // Map scroll progress to steps (0-3), first 10% is header, remaining 90% split across 4 steps
      const stepProgress = Math.max(0, (v - 0.08) / 0.88);
      const step = Math.min(3, Math.floor(stepProgress * 4));
      setActiveStep(step);
    });
    return unsubscribe;
  }, [scrollYProgress]);

  // Progress bar width
  const progressWidth = useTransform(scrollYProgress, [0.08, 0.96], ['0%', '100%']);

  return (
    <section ref={containerRef} className="relative" style={{ height: '350vh', background: '#0A1628' }} data-testid="how-it-works-section">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Section label */}
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-6"
          style={{ color: '#14B8A6' }}>
          How It Works
        </motion.span>

        {/* Step content area */}
        <div className="relative h-[320px] sm:h-[300px] flex items-center justify-center w-full max-w-3xl mx-auto px-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.step}
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
              initial={false}
              animate={{
                opacity: activeStep === i ? 1 : 0,
                y: activeStep === i ? 0 : (activeStep > i ? -60 : 60),
                scale: activeStep === i ? 1 : 0.92,
                filter: activeStep === i ? 'blur(0px)' : 'blur(6px)',
              }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>

              {/* Step number - BIG and SOLID */}
              <motion.span
                className="text-[100px] sm:text-[130px] lg:text-[160px] font-black leading-none tracking-tighter"
                style={{ color: s.color, opacity: 0.2 }}
                animate={{ opacity: activeStep === i ? 0.2 : 0 }}
                transition={{ duration: 0.6 }}>
                {s.step}
              </motion.span>

              {/* Title */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" style={{ color: '#F7FAFC' }}>
                {s.title}
              </h2>

              {/* Description */}
              <p className="text-sm sm:text-base lg:text-lg max-w-lg" style={{ color: '#8BA4C4' }}>
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Icon row - all 4 steps always visible, active one highlighted */}
        <div className="flex items-center gap-6 sm:gap-10 mt-10">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.step}
              className="flex flex-col items-center gap-2"
              animate={{
                opacity: activeStep === i ? 1 : 0.3,
                scale: activeStep === i ? 1 : 0.85,
              }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
              <motion.div
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
                animate={{
                  background: activeStep === i ? `${s.color}20` : 'rgba(255,255,255,0.04)',
                  borderColor: activeStep === i ? `${s.color}50` : 'rgba(255,255,255,0.08)',
                  boxShadow: activeStep === i ? `0 8px 24px ${s.color}25` : '0 2px 8px rgba(0,0,0,0.1)',
                }}
                style={{ border: '1.5px solid rgba(255,255,255,0.08)' }}
                transition={{ duration: 0.5 }}>
                <s.icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: activeStep === i ? s.color : '#475569' }} />
              </motion.div>
              <span className="text-[10px] font-bold hidden sm:block" style={{ color: activeStep === i ? '#E2E8F0' : '#475569' }}>
                {s.title}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-48 sm:w-64">
          <div className="h-[3px] rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <motion.div className="h-full rounded-full" style={{ width: progressWidth, background: '#14B8A6' }} />
          </div>
          <div className="flex justify-between mt-3">
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full"
                animate={{
                  background: activeStep >= i ? '#14B8A6' : 'rgba(255,255,255,0.15)',
                  scale: activeStep === i ? 1.4 : 1,
                }}
                transition={{ duration: 0.3 }} />
            ))}
          </div>
        </div>

        {/* Learn more CTA at bottom */}
        <motion.div
          className="absolute bottom-6"
          animate={{ opacity: activeStep === 3 ? 1 : 0, y: activeStep === 3 ? 0 : 10 }}
          transition={{ duration: 0.5 }}>
          <Button onClick={() => navigate('/how-it-works')}
            className="h-10 px-5 text-sm font-bold rounded-xl"
            style={{ background: 'rgba(255,255,255,0.06)', color: '#14B8A6', border: '1px solid rgba(255,255,255,0.12)' }}
            data-testid="learn-more-btn">
            Learn More <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          className="absolute bottom-6"
          animate={{ opacity: activeStep < 3 ? 0.4 : 0, y: [0, 6, 0] }}
          transition={{ y: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }, opacity: { duration: 0.3 } }}>
          <ChevronRight className="w-5 h-5 rotate-90" style={{ color: 'rgba(255,255,255,0.3)' }} />
        </motion.div>
      </div>
    </section>
  );
}


export default function LandingPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const mockupScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.92]);

  const stat1 = useCounter(10000, 2000);
  const stat2 = useCounter(93, 1500);
  const stat3 = useCounter(49, 1200);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Inject keyframe animations */}
      <style>{`
        @keyframes float-slow { 0%,100%{transform:translate(0,0)} 50%{transform:translate(30px,-40px)} }
        @keyframes float-medium { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-20px,30px)} }
        @keyframes float-fast { 0%,100%{transform:translate(0,0)} 50%{transform:translate(15px,-25px)} }
        @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.3} 100%{transform:scale(1.6);opacity:0} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .animate-float-slow { animation: float-slow 12s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 10s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 8s ease-in-out infinite; }
        .card-hover { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease; }
        .card-hover:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0,0,0,0.08); }
      `}</style>

      <MarketingNav />

      {/* ───────────── HERO (Dark + Parallax) ───────────── */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-8 lg:pt-48 lg:pb-12" data-testid="hero-section"
        style={{ background: 'linear-gradient(180deg, #0A1628 0%, #0D1E3A 100%)' }}>
        <FloatingOrbs />
        <AnimatedLines className="top-10 right-10 w-[300px] h-[300px] hidden lg:block" />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative max-w-5xl mx-auto px-6 text-center">
          {/* Badge - appears first */}
          <Reveal delay={0}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8"
              style={{ background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(13,148,136,0.2)' }}>
              <Zap className="w-3.5 h-3.5" style={{ color: '#14B8A6' }} />
              <span className="text-xs font-semibold tracking-wide" style={{ color: '#14B8A6' }}>
                Career Intelligence Platform
              </span>
            </div>
          </Reveal>

          {/* Heading - word by word stagger */}
          <Reveal delay={0.15} y={72}>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] mb-6"
              style={{ color: '#F7FAFC' }}>
              <motion.span className="inline-block"
                initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>Stop guessing.</motion.span>
              <br />
              <motion.span className="inline-block" style={{ color: '#14B8A6' }}
                initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}>Know</motion.span>
              <motion.span className="inline-block ml-3"
                initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}>your career.</motion.span>
            </h1>
          </Reveal>

          {/* Subtitle */}
          <Reveal delay={0.4}>
            <p className="text-base sm:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto mb-10"
              style={{ color: '#9DB5DC' }}>
              Kairera decodes your strengths, maps your potential, and builds a
              personalized career strategy, so every move is backed by intelligence, not instinct.
            </p>
          </Reveal>

          {/* CTA buttons */}
          <Reveal delay={0.55}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: '0 8px 35px rgba(13,148,136,0.4)' }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                className="rounded-xl">
                <Button onClick={() => navigate('/register')}
                  className="h-13 px-8 text-base font-bold rounded-xl"
                  style={{ background: '#0D9488', color: '#fff', boxShadow: '0 4px 25px rgba(13,148,136,0.35)', height: '52px' }}
                  data-testid="hero-get-started-btn">
                  Start Free <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, borderColor: 'rgba(255,255,255,0.25)' }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                className="rounded-xl">
                <Button onClick={() => navigate('/how-it-works')}
                  className="h-13 px-8 text-base font-medium rounded-xl"
                  style={{ height: '52px', background: 'rgba(255,255,255,0.06)', color: '#C4D3EE', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid="hero-how-it-works-btn">
                  <Play className="w-4 h-4 mr-2" /> See How It Works
                </Button>
              </motion.div>
            </div>
          </Reveal>

          {/* Animated Stats */}
          <Reveal delay={0.7}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
              <div className="flex items-center gap-2" ref={stat1.ref}>
                <span className="text-lg font-extrabold tabular-nums" style={{ color: '#14B8A6' }}>
                  {stat1.count.toLocaleString()}+
                </span>
                <span className="text-xs font-medium" style={{ color: '#7595CC' }}>Career Assessments</span>
              </div>
              <div className="flex items-center gap-2" ref={stat2.ref}>
                <span className="text-lg font-extrabold tabular-nums" style={{ color: '#14B8A6' }}>
                  {stat2.count}%
                </span>
                <span className="text-xs font-medium" style={{ color: '#7595CC' }}>User Satisfaction</span>
              </div>
              <div className="flex items-center gap-2" ref={stat3.ref}>
                <span className="text-lg font-extrabold tabular-nums" style={{ color: '#14B8A6' }}>
                  {stat3.count === 49 ? '4.9' : (stat3.count / 10).toFixed(1)}/5
                </span>
                <span className="text-xs font-medium" style={{ color: '#7595CC' }}>Average Rating</span>
              </div>
            </div>
          </Reveal>
        </motion.div>

        {/* ── Hero Illustration + Platform Mockups ── */}
        <motion.div style={{ scale: mockupScale }} className="relative max-w-6xl mx-auto px-6 mt-16">
          <Reveal delay={0.85} y={60}>
            <div className="relative">
              {/* Main dashboard screenshot */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/10">
                <div className="absolute inset-0 opacity-30 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(13,148,136,0.1) 0%, transparent 30%)' }} />
                <img src="/mockups/dashboard-mock.jpg" alt="Kairera Dashboard" className="w-full h-auto" />
              </div>
            </div>
          </Reveal>
        </motion.div>
      </section>

      {/* ───────────── TRUST BAR (White, slide in) ───────────── */}
      <section className="py-14 bg-white border-b" style={{ borderColor: '#f1f5f9' }}>
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <p className="text-center text-[10px] font-bold uppercase tracking-[0.25em] mb-6" style={{ color: '#94a3b8' }}>
              Trusted by professionals at
            </p>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {['Google', 'McKinsey', 'Deloitte', 'Amazon', 'Microsoft', 'Goldman Sachs'].map((co, i) => (
              <Reveal key={co} delay={0.05 * i}>
                <span className="text-sm font-bold tracking-wide opacity-40" style={{ color: '#334155' }}>{co}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── FEATURE HIGHLIGHTS (Apple-style sequential reveal) ───────────── */}
      <section className="py-24 lg:py-36 bg-white relative overflow-hidden" data-testid="features-preview">
        <AnimatedLines className="-left-20 top-1/4 w-[400px] h-[400px] hidden lg:block" />
        <div className="max-w-6xl mx-auto px-6 relative">
          {/* Section header - slides in first */}
          <Reveal y={60}>
            <div className="text-center mb-20">
              <motion.span
                initial={{ opacity: 0, letterSpacing: '0.1em' }}
                whileInView={{ opacity: 1, letterSpacing: '0.25em' }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-4" style={{ color: '#0D9488' }}>
                The Platform
              </motion.span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5" style={{ color: '#0D1E3A' }}>
                Everything you need to own your career
              </h2>
              <p className="text-base lg:text-lg max-w-xl mx-auto" style={{ color: '#64748b' }}>
                A complete intelligence system: from self-discovery to strategy.
              </p>
            </div>
          </Reveal>

          {/* Feature cards - staggered cascade */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Brain, color: '#0D9488', title: 'Career Intelligence Engine', desc: 'Multi-phase assessments that decode your decision patterns, values, and hidden strengths.' },
              { icon: Users, color: '#F5841F', title: 'Expert Advisory Panel', desc: 'Your results reviewed by a specialized panel of career experts tailored to your industry.' },
              { icon: Target, color: '#3B82F6', title: 'Career Persona', desc: 'A unique career archetype that maps your decision-making DNA to optimal career paths.' },
            ].map((card, i) => {
              const stagger = 0.18 * i;
              return (
                <Reveal key={card.title} delay={0.15 + stagger} y={64}>
                  <motion.div
                    whileHover={{ y: -8, boxShadow: '0 24px 48px rgba(0,0,0,0.08)', borderColor: `${card.color}30` }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                    className="rounded-2xl p-8 h-full relative group"
                    style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }}>

                    {/* Subtle gradient glow on hover */}
                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                      style={{ background: `radial-gradient(circle at 30% 20%, ${card.color}06, transparent 60%)` }} />

                    {/* Icon with spring bounce */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
                      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.35 + stagger, type: 'spring', stiffness: 200, damping: 15 }}
                      whileHover={{ rotate: [0, -8, 8, -4, 0], transition: { duration: 0.6 } }}
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 relative"
                      style={{ background: `${card.color}10` }}>
                      <card.icon className="w-7 h-7" style={{ color: card.color }} />
                    </motion.div>

                    {/* Title with slide-in */}
                    <motion.h3
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7, delay: 0.5 + stagger, ease: [0.22, 1, 0.36, 1] }}
                      className="text-lg font-bold mb-3" style={{ color: '#0D1E3A' }}>
                      {card.title}
                    </motion.h3>

                    {/* Description with gentle fade */}
                    <motion.p
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.65 + stagger, ease: 'easeOut' }}
                      className="text-sm leading-relaxed" style={{ color: '#64748b' }}>
                      {card.desc}
                    </motion.p>

                    {/* Bottom accent line */}
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.8 + stagger, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute bottom-0 left-0 right-0 h-[2px] rounded-b-2xl origin-left"
                      style={{ background: `linear-gradient(90deg, ${card.color}, transparent)` }} />
                  </motion.div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.8}>
            <div className="text-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                className="inline-block">
                <Button onClick={() => navigate('/features')}
                  className="h-11 px-6 text-sm font-bold rounded-xl"
                  style={{ background: '#f8fafc', color: '#0D9488', border: '1px solid #e2e8f0' }}
                  data-testid="view-all-features-btn">
                  View All Features <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── ILLUSTRATION BREAK ───────────── */}
      <section className="py-16 lg:py-20 relative overflow-hidden" style={{ background: '#0A1628' }}>
        <FloatingOrbs />
        <div className="max-w-5xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-12">
          <Reveal className="flex-1">
            <motion.div whileHover={{ scale: 1.02 }} transition={{ type: 'spring', stiffness: 200 }}>
              <img src="/mockups/hero-illustration.png" alt="Career Intelligence"
                className="w-full max-w-md mx-auto rounded-2xl shadow-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }} />
            </motion.div>
          </Reveal>
          <Reveal delay={0.2} className="flex-1 text-center lg:text-left">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-3" style={{ color: '#14B8A6' }}>
              Intelligence-Driven
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-4" style={{ color: '#F7FAFC' }}>
              Your career data, decoded
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color: '#9DB5DC' }}>
              Kairera transforms your career history, assessment responses, and decision patterns into
              actionable intelligence. Every recommendation is personalized to your unique career DNA.
            </p>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button onClick={() => navigate('/register')}
                className="h-11 px-6 text-sm font-bold rounded-xl"
                style={{ background: '#0D9488', color: '#fff' }}>
                Discover Your Persona <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── HOW IT WORKS (Apple scroll-pinned sequential reveal) ───────────── */}
      <HowItWorksSequential navigate={navigate} />

      {/* ───────────── TESTIMONIALS (Apple-style staggered reveal) ───────────── */}
      <section className="py-24 lg:py-32 relative" style={{ background: '#0A1628' }} data-testid="social-proof-section">
        <AnimatedLines className="-right-20 top-10 w-[400px] h-[400px] hidden lg:block" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <Reveal y={60}>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-16 text-center" style={{ color: '#F7FAFC' }}>
              Professionals trust Kairera
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Sarah Chen', role: 'VP of Product, Series C Startup', quote: 'Kairera showed me patterns in my career decisions I never noticed. The persona report was eerily accurate.' },
              { name: 'Marcus Rodriguez', role: 'Senior Consultant, Big 4', quote: 'The expert panel gave me strategic advice I used to pay thousands for. The assessment system is genuinely rigorous.' },
              { name: 'Priya Sharma', role: 'Engineering Manager, FAANG', quote: 'I saw my grade report and it pinpointed exactly where I was underinvesting in my growth. Game changer.' },
            ].map((t, i) => (
              <Reveal key={t.name} delay={0.15 + i * 0.2} y={56}>
                <motion.div
                  whileHover={{ y: -6, borderColor: 'rgba(255,255,255,0.18)', boxShadow: '0 16px 40px rgba(0,0,0,0.2)' }}
                  transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                  className="rounded-2xl p-7"
                  style={{ background: 'rgba(27,52,97,0.25)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(8px)' }}>
                  {/* Stars with individual pop-in */}
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, j) => (
                      <motion.div key={j}
                        initial={{ opacity: 0, scale: 0, rotate: -90 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + i * 0.2 + j * 0.06, type: 'spring', stiffness: 300, damping: 12 }}>
                        <Star className="w-3.5 h-3.5 fill-current" style={{ color: '#F5841F' }} />
                      </motion.div>
                    ))}
                  </div>
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.55 + i * 0.2, ease: 'easeOut' }}
                    className="text-[13px] leading-relaxed mb-6 italic" style={{ color: '#C4D3EE' }}>
                    "{t.quote}"
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.7 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}>
                    <p className="text-sm font-bold" style={{ color: '#F7FAFC' }}>{t.name}</p>
                    <p className="text-[11px]" style={{ color: '#7595CC' }}>{t.role}</p>
                  </motion.div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── CTA (Apple cinematic reveal) ───────────── */}
      <section className="py-28 lg:py-36 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal y={72}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5" style={{ color: '#0D1E3A' }}>
              Your next career move<br />should be your <span style={{ color: '#0D9488' }}>smartest</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2} y={40}>
            <p className="text-base lg:text-lg mb-10 max-w-xl mx-auto" style={{ color: '#64748b' }}>
              Join thousands of professionals who've replaced career guesswork with career intelligence.
            </p>
          </Reveal>
          <Reveal delay={0.4} y={32}>
            <motion.div
              whileHover={{ scale: 1.05, boxShadow: '0 8px 40px rgba(13,148,136,0.35)' }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 15 }}
              className="inline-block rounded-xl">
              <Button onClick={() => navigate('/register')}
                className="h-14 px-10 text-base font-bold rounded-xl"
                style={{ background: '#0D9488', color: '#fff', boxShadow: '0 4px 30px rgba(13,148,136,0.25)' }}
                data-testid="cta-get-started-btn">
                Start Your Free Assessment <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
            <p className="text-xs mt-5" style={{ color: '#94a3b8' }}>No credit card required. Free forever plan available.</p>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
